/*
  EOJ Reconciliation Utilities

  This module handles End-of-Journey reconciliation migration and flag propagation.
  It manages the legacy reconciliation workflow node configuration.
*/

import type { JourneyDocument, ReconciliationWorkflowNode } from '../../types';

/**
 * Handles End-of-Journey reconciliation migration and flag propagation
 *
 * This function:
 * 1. Moves top-level reconciliation_conditions to terminal recon node config
 * 2. Marks all outcome nodes connected from terminal recon as reconciliation terminal
 * 3. Propagates flags through review nodes in the reconciliation path
 *
 * @param doc - The journey document to process
 * @returns The document with EOJ reconciliation properly configured
 */
export function handleEojReconciliation(doc: JourneyDocument): JourneyDocument {
  const terminalReconId = doc.terminal_reconciliation_workflow_node_id;
  if (!terminalReconId || !doc.nodes[terminalReconId]) return doc;

  const terminalReconNode = doc.nodes[terminalReconId] as ReconciliationWorkflowNode;
  if (terminalReconNode.type !== 'reconciliation_workflow') return doc;

  terminalReconNode.config = {
    ...(terminalReconNode.config || {}),
    workflow_token: terminalReconNode.config?.workflow_token,
    workflow_type: terminalReconNode.config?.workflow_type,
    workflow_version: terminalReconNode.config?.workflow_version,
    legacy: false,
    terminal: true,
  };

  // Move top-level reconciliation_conditions to terminal recon node config
  const reconConditions = (doc as any).reconciliation_conditions;
  if (reconConditions && Array.isArray(reconConditions) && reconConditions.length > 0) {
    terminalReconNode.config.reconciliation_conditions = reconConditions;
    terminalReconNode.config.legacy = true;
    doc.nodes[terminalReconId] = terminalReconNode;
  }
  // For every edge leaving recon, mark target nodes as reconciliation terminal nodes
  Object.values(doc.edges).forEach(e => {
    if (e.from.node === terminalReconId) {
      const t = doc.nodes[e.to.node] as any;
      if (!t) return;
      t.config = { ...(t.config || {}), reconciliation: true, terminal: true };
      doc.nodes[e.to.node] = t;
    }
  });

  // Also propagate flags for manual review path: edges leaving review nodes connected from recon
  const reviewTargets = new Set<string>();
  Object.values(doc.edges).forEach(e => {
    if (e.from.node === terminalReconId) {
      const target = doc.nodes[e.to.node];
      if (target?.type === 'review') reviewTargets.add(e.to.node);
    }
  });
  Object.values(doc.edges).forEach(e => {
    if (reviewTargets.has(e.from.node)) {
      const t = doc.nodes[e.to.node] as any;
      if (!t) return;
      t.config = { ...(t.config || {}), reconciliation: true, terminal: true };
      doc.nodes[e.to.node] = t;
    }
  });

  return doc;
}

